'use client';

import styles from './index.module.scss';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/i18n';
import EcoButton from 'eco-design/base/eco-button';
import EcoImg from 'eco-design/base/eco-img';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper';
import 'swiper/scss';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';
import ReferButton from '../ReferButton';

interface UserReview {
    img: string;
    content: string;
    userName: string;
}

const localeImgMap: any = {
    eu: {
        1: 'https://websiteoss.ecoflow.com/figma-uploads/1767084132368-fcyi5h-compressed-Frame%202094654803.jpg',
        2: 'https://websiteoss.ecoflow.com/figma-uploads/1767084201785-3e5rw6-compressed-Frame%202094654804.jpg',
        3: 'https://websiteoss.ecoflow.com/figma-uploads/1767084213997-in570f-compressed-Frame%202094654805.jpg',
    },
    us: {
        1: 'https://websiteoss.ecoflow.com/figma-uploads/1767084226742-aqtz60-compressed-Frame%202094654807.jpg',
        2: 'https://websiteoss.ecoflow.com/figma-uploads/1767084241535-6pnpx9-compressed-Frame%202094654808.jpg',
        3: 'https://websiteoss.ecoflow.com/figma-uploads/1767084253818-hkhpra-compressed-Frame%202094654809.jpg',
    },
    jp: {
        1: 'https://websiteoss.ecoflow.com/figma-uploads/1767084272283-p649ki-compressed-Frame%202094654810.jpg',
        2: 'https://websiteoss.ecoflow.com/figma-uploads/1767084281528-mqo9zg-compressed-Frame%202094654811.jpg',
        3: 'https://websiteoss.ecoflow.com/figma-uploads/1767084293201-x3ydbj-compressed-Frame%202094654813.jpg',
    },
    ph: {
        1: 'https://websiteoss.ecoflow.com/figma-uploads/1767084306850-zbgsz7-compressed-Frame%202094654815.jpg',
        2: 'https://websiteoss.ecoflow.com/figma-uploads/1767084734839-f74bf1-compressed-Frame%202094654816.jpg',
        3: 'https://websiteoss.ecoflow.com/figma-uploads/1767084745303-w7qsws-compressed-Frame%202094654817.jpg',
    },
    mx: {
        1: 'https://websiteoss.ecoflow.com/figma-uploads/1767084764724-l9tsap-compressed-Frame%202094654822.jpg',
        2: 'https://websiteoss.ecoflow.com/figma-uploads/1767084777465-zce0j5-compressed-Frame%202094654821.jpg',
        3: 'https://websiteoss.ecoflow.com/figma-uploads/1767084788853-j2bz19-compressed-Frame%202094654820.jpg',
    },
    kr: {
        1: 'https://websiteoss.ecoflow.com/figma-uploads/1767084807966-4cjh8v-compressed-Frame%202094654824.jpg',
        2: 'https://websiteoss.ecoflow.com/figma-uploads/1767084817503-85qi5u-compressed-Frame%202094654825.jpg',
        3: 'https://websiteoss.ecoflow.com/figma-uploads/1767084826612-xzfklq-compressed-Frame%202094654826.jpg',
    },
    za: {
        1: 'https://websiteoss.ecoflow.com/figma-uploads/1767084836778-98q97b-compressed-Frame%202094654830.jpg',
        2: 'https://websiteoss.ecoflow.com/figma-uploads/1767084851206-5acdra-compressed-Frame%202094654831.jpg',
        3: 'https://websiteoss.ecoflow.com/figma-uploads/1767084861050-fomxlu-compressed-Frame%202094654832.jpg',
    },
};

localeImgMap.uk = localeImgMap.eu;
localeImgMap.de = localeImgMap.eu;
localeImgMap.fr = localeImgMap.eu;
localeImgMap.es = localeImgMap.eu;
localeImgMap.it = localeImgMap.eu;

localeImgMap.ca = localeImgMap.us;
localeImgMap.au = localeImgMap.us;

export default function RealUsers({ showReferButton = false }: { showReferButton?: boolean }) {
    const { t } = useLang('ReauUsers');
    const { locale } = getLocal();

    const userReviews: UserReview[] = [
        {
            img: (localeImgMap[locale] || localeImgMap['us'])[1],
            content: t('stories_context1'),
            userName: t('stories_user1'),
        },
        {
            img: (localeImgMap[locale] || localeImgMap['us'])[2],
            content: t('stories_context2'),
            userName: t('stories_user2'),
        },
        {
            img: (localeImgMap[locale] || localeImgMap['us'])[3],
            content: t('stories_context3'),
            userName: t('stories_user3'),
        },
    ];

    return (
        <div className={`${styles.RealUsers} GtmModuleData-section`}>
            <div className="sh-layout">
                <div className={`real-users-header ${showReferButton ? '' : 'referree'}`}>
                    <h2 className="real-users-title">{t('title')}</h2>
                    <p className="real-users-desc">{t('desc')}</p>
                </div>

                <div className="real-users-swiper-wrapper">
                    <Swiper
                        className="real-users-swiper"
                        slidesPerView="auto"
                        spaceBetween={20}
                        navigation={userReviews.length > 3}
                        pagination={{
                            clickable: true,
                        }}
                        modules={[Navigation]}
                        breakpoints={{
                            320: {
                                slidesPerView: 'auto',
                                spaceBetween: 12,
                            },
                            768: {
                                slidesPerView: 'auto',
                                spaceBetween: 16,
                            },
                            1024: {
                                slidesPerView: 3,
                                spaceBetween: 20,
                            },
                        }}
                    >
                        {userReviews.map((review, index) => (
                            <SwiperSlide className="real-users-slide" key={index}>
                                <div className="review-card">
                                    <div className="review-img">
                                        <EcoImg pc={review.img} />
                                    </div>
                                    <div className="review-content">
                                        <p className="review-text">{review.content}</p>
                                        <div className="review-user">{review.userName}</div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {showReferButton && (
                    <div className="cta-wrapper">
                        <ReferButton lTheme="black" />
                    </div>
                )}
            </div>
        </div>
    );
}
